/**
 * useMobileChatGestures Hook
 *
 * Custom hook for mobile chat gesture interactions
 */

import { useDrag } from "@use-gesture/react";
import { useState } from "react";
import { clamp, hapticFeedback } from "@/lib/utils/gesture.utils";

interface UseMobileChatGesturesOptions {
	onClose: () => void;
	disabled?: boolean;
}

export function useMobileChatGestures(options: UseMobileChatGesturesOptions) {
	const { onClose, disabled = false } = options;

	const [dragState, setDragState] = useState({
		y: 0,
		isDragging: false,
		opacity: 1,
	});

	const bind = useDrag(
		({
			active,
			movement: [, my],
			velocity: [, vy],
			direction: [, dy],
			cancel,
			event,
		}) => {
			if (disabled) return;

			// Check if we're at the top of the chat scroll
			const target = event?.target as HTMLElement;
			const scrollContainer = target?.closest("[data-chat-scroll]");
			const isAtTop = !scrollContainer || scrollContainer.scrollTop === 0;

			// Only allow downward drags when at top of scroll
			if (isAtTop && dy > 0 && my > 0) {
				// Calculate opacity based on drag distance
				const opacity = clamp(1 - my / 300, 0.7, 1);

				setDragState({
					y: my,
					isDragging: active,
					opacity,
				});

				// Check if should close
				if (!active) {
					if (my > 100 || vy > 0.5) {
						hapticFeedback("light");
						onClose();
						cancel();
					} else {
						// Snap back
						setDragState({
							y: 0,
							isDragging: false,
							opacity: 1,
						});
					}
				}
			} else if (!active) {
				// Reset if gesture is not downward
				setDragState({
					y: 0,
					isDragging: false,
					opacity: 1,
				});
			}
		},
		{
			filterTaps: true,
			axis: "y",
			rubberband: true,
			pointer: { touch: true },
		},
	);

	return {
		dragState,
		bind,
	};
}
